import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { ParametrosService } from 'src/app/services/parametros.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';
import { UserService } from '../../../services/user.service';
import {
  PERFIL_OFERTAS_COPY,
  formatDineroCop,
  formatMontoMillones,
  formatTasaLabel,
  perfilOfertasIntro,
} from '../../data/perfil-ofertas.content';
import { MicroempresarioService } from '../../services/microempresario.service';
import {
  esOfertaBancoldex,
  esTickSinTecho,
  filtrarOfertas,
  idsPresentesEnLista,
  MAX_COMPARAR,
  ordenarOfertas,
  OrdenOfertas,
  resolverFiltroTick,
  toggleIdSeleccionado,
} from './ofertas-filtros.util';
import {
  OfertasModalId,
  PerfilOfertasModalesComponent,
} from './perfil-ofertas-modales.component';
import {
  OfertaMotivoRechazoMock,
  OfertaPropuestaMock,
} from './perfil-ofertas.model';
import { UtmTrackingService } from 'src/app/services/utm-tracking.service';

const TOPE_MONTO = 200;
const TOPE_TASA = 60;
const PAR_078 = 'PAR-078';
const PAR_079 = 'PAR-079';

const ASSET_HANDLE = 'assets/img/perfil/ofertas/filtro-handle.svg';
const ASSET_BANCOLDEX = 'assets/img/perfil/ofertas/icon-bancoldex.svg';
const ASSET_COMPARAR = 'assets/img/perfil/ofertas/icon-comparar.svg';

/**
 * HU 8209 — ofertas de crédito (listar / comparar / tomar / rechazar).
 */
@Component({
  selector: 'app-perfil-ofertas',
  templateUrl: './perfil-ofertas.component.html',
  styleUrls: ['./perfil-ofertas.component.scss'],
  standalone: false,
})
export class PerfilOfertasComponent implements OnInit {
  @ViewChild(PerfilOfertasModalesComponent)
  private modales?: PerfilOfertasModalesComponent;

  readonly copy = PERFIL_OFERTAS_COPY;
  readonly ticksMonto = PERFIL_OFERTAS_COPY.ticksMontoMillones;
  readonly ticksTasa = PERFIL_OFERTAS_COPY.ticksTasaPorcentaje;
  readonly assetHandle = ASSET_HANDLE;
  readonly assetBancoldex = ASSET_BANCOLDEX;
  readonly assetComparar = ASSET_COMPARAR;
  readonly ordenes: { id: OrdenOfertas; label: string }[] = [
    { id: 'monto', label: PERFIL_OFERTAS_COPY.ordenMayorMonto },
    { id: 'tasa', label: PERFIL_OFERTAS_COPY.ordenMenorTasa },
    { id: 'plazo', label: PERFIL_OFERTAS_COPY.ordenMayorPlazo },
  ];

  idSolicitud = '';
  todas: OfertaPropuestaMock[] = [];
  cantidadTotal = 0;
  diasRestantes: number | null = null;
  loading = true;
  error = false;
  enviando = false;

  par078 = PERFIL_OFERTAS_COPY.par078Fallback;
  par079 = PERFIL_OFERTAS_COPY.par079Fallback;
  motivos: OfertaMotivoRechazoMock[] = [];

  filtroMonto: number | null = null;
  filtroTasa: number | null = null;
  orden: OrdenOfertas = '';
  idExpandida: number | null = null;
  idsSeleccionadas: number[] = [];
  modal: OfertasModalId = null;
  ofertaActiva: OfertaPropuestaMock | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly microempresario: MicroempresarioService,
    private readonly user: UserService,
    private readonly parametros: ParametrosService,
    private readonly bancoldex: BancoldexService,
    private readonly sweetAlert: SweetAlertService,
    private readonly analyticsTag: AnalyticsTagService,
    private readonly utmTracking: UtmTrackingService
  ) {}

  ngOnInit(): void {
    this.idSolicitud = this.route.snapshot.paramMap.get('id') ?? '';
    this.cargarParametrosModal();
    this.cargarMotivos();
    this.cargar();
  }

  get visibles(): OfertaPropuestaMock[] {
    const lista = filtrarOfertas(this.todas, this.filtroMonto, this.filtroTasa);
    return ordenarOfertas(lista, this.orden);
  }

  get seleccionadas(): OfertaPropuestaMock[] {
    return this.idsSeleccionadas
      .map((id) => this.visibles.find((o) => o.idPropuesta === id))
      .filter((o): o is OfertaPropuestaMock => !!o);
  }

  get intro(): string {
    return perfilOfertasIntro(this.cantidadTotal || this.todas.length);
  }

  get esUnaOferta(): boolean {
    return this.visibles.length === 1;
  }

  get esLista(): boolean {
    return this.visibles.length > 1;
  }

  get filtrosInteractivos(): boolean {
    return this.cantidadTotal > 1;
  }

  get esListaFiltrada(): boolean {
    return this.cantidadTotal > this.visibles.length;
  }

  get oferta(): OfertaPropuestaMock | null {
    return this.visibles[0] ?? null;
  }

  get labelMontoFiltro(): string {
    if (this.esLista || this.filtroMonto != null) {
      return this.labelRango(this.ticksMonto, this.filtroMonto, (t) => this.labelTickMonto(t));
    }
    return this.oferta ? formatMontoMillones(this.oferta.monto) : '';
  }

  get labelTasaFiltro(): string {
    if (this.esLista || this.filtroTasa != null || this.esListaFiltrada) {
      return this.labelRango(this.ticksTasa, this.filtroTasa, (t) => this.labelTickTasa(t));
    }
    return this.oferta ? formatTasaLabel(this.oferta.tasa) : '';
  }

  get labelResultados(): string {
    const n = this.visibles.length;
    return n === 1 ? this.copy.unResultado : this.copy.nResultados(n);
  }

  get mostrarTodasVisible(): boolean {
    return this.esListaFiltrada;
  }

  get montoHandlePct(): number {
    if (this.filtroMonto != null) {
      return this.pctTick(this.ticksMonto, this.filtroMonto);
    }
    if (this.esLista) {
      return 100;
    }
    return this.pctEnRango(
      this.oferta ? this.oferta.monto / 1_000_000 : 0,
      this.ticksMonto[0],
      this.ticksMonto[this.ticksMonto.length - 1]
    );
  }

  get tasaHandlePct(): number {
    if (this.filtroTasa != null) {
      return this.pctTick(this.ticksTasa, this.filtroTasa);
    }
    if (this.esLista || this.esListaFiltrada) {
      return 100;
    }
    return this.pctEnRango(
      this.oferta?.tasa ?? 0,
      this.ticksTasa[0],
      this.ticksTasa[this.ticksTasa.length - 1]
    );
  }

  get muestraRangoMonto(): boolean {
    return this.esLista || this.filtroMonto != null;
  }

  get muestraRangoTasa(): boolean {
    return this.esLista || this.filtroTasa != null || this.esListaFiltrada;
  }

  get montoFill(): { start: number; end: number } {
    if (this.muestraRangoMonto) {
      return { start: 0, end: this.montoHandlePct };
    }
    return { start: 0, end: 0 };
  }

  get tasaFill(): { start: number; end: number } {
    if (this.muestraRangoTasa) {
      return { start: 0, end: this.tasaHandlePct };
    }
    return { start: 0, end: 0 };
  }

  onAtras(): void {
    this.modal = 'salir';
  }

  onCerrarModal(): void {
    this.modal = null;
  }

  onSalirHome(): void {
    this.modal = null;
    void this.router.navigateByUrl('/microempresario/home');
  }

  onTomarOferta(oferta?: OfertaPropuestaMock | null, ctaElementId?: string): void {
    this.ofertaActiva = oferta ?? this.oferta;
    if (!this.ofertaActiva) {
      return;
    }
    if (ctaElementId) {
      this.analyticsTag.trackCtaClick({
        ctaLabel: this.copy.tomarOferta,
        ctaSection: 'ofertas',
        ctaComponent: 'perfil-ofertas',
        ctaElementId,
        hu: '8209',
      });
    }
    this.modal = 'tomar1';
  }

  onTomarSiguiente(): void {
    this.modal = 'tomar2';
  }

  onTomarConfirmar(): void {
    const oferta = this.ofertaActiva;
    const idUsuario = this.user.getUser()?.idUsuario;
    if (!oferta || !idUsuario) {
      void this.sweetAlert.errorModal('Error!', true, 'No se pudo identificar la oferta o el usuario');
      return;
    }
    this.enviando = true;
    this.microempresario
      .aceptarPropuesta({
        idSolicitud: this.idSolicitud,
        idPropuesta: oferta.idPropuesta,
        idUsuario: +idUsuario,
      })
      .subscribe({
        next: (data: { exitoso?: boolean; error?: string }) => {
          this.enviando = false;
          if (data?.exitoso) {
            this.utmTracking.registrarOfertaAceptada(String(this.idSolicitud || ''));
            this.modal = null;
            this.modales?.resetTrasExito();
            void this.router.navigateByUrl(`/microempresario/solicitudAceptada/${this.idSolicitud}`);
            return;
          }
          void this.sweetAlert.errorModal('Error!', true, data?.error || 'No se pudo aceptar la oferta');
        },
        error: () => {
          this.enviando = false;
          void this.sweetAlert.errorModal('Error!', true, 'No se pudo aceptar la oferta');
        },
      });
  }

  onGarantias(oferta: OfertaPropuestaMock, event?: Event): void {
    event?.stopPropagation();
    if (!this.tieneGarantias(oferta)) {
      return;
    }
    this.ofertaActiva = oferta;
    this.modal = 'garantias';
  }

  onRechazarAbrir(): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.copy.rechazarOfertas,
      ctaSection: 'ofertas',
      ctaComponent: 'perfil-ofertas',
      ctaElementId: 'btn-ofertas-rechazar',
      hu: '8209',
    });
    this.modal = 'rechazar';
  }

  onRechazarConfirmar(payload: {
    idMotivo: number;
    detalle: string;
    condiciones: number[];
  }): void {
    const idUsuario = this.user.getUser()?.idUsuario;
    if (!idUsuario || !payload?.idMotivo) {
      void this.sweetAlert.errorModal('Error!', true, 'Datos de rechazo incompletos');
      return;
    }
    this.enviando = true;
    this.microempresario
      .negarPropuesta({
        idSolicitud: this.idSolicitud,
        idTipoNegacionPropuestas: String(payload.idMotivo),
        idUsuario: +idUsuario,
        configuracionCondicionesFinancieras: (payload.condiciones ?? []).map((id) => Number(id)),
        otro: payload.detalle || '',
      })
      .subscribe({
        next: (response: { exitoso?: boolean; mensaje?: string; error?: string }) => {
          this.enviando = false;
          if (response?.exitoso) {
            this.modal = null;
            this.modales?.resetTrasExito();
            void this.sweetAlert.successModal(response.mensaje || 'Ofertas rechazadas');
            void this.router.navigateByUrl('/microempresario/home');
            return;
          }
          void this.sweetAlert.errorModal('Error!', true, response?.error || 'Se ha presentado un error');
        },
        error: () => {
          this.enviando = false;
          void this.sweetAlert.errorModal('Error!', true, 'Se ha presentado un error');
        },
      });
  }

  onFabComparar(): void {
    if (this.seleccionadas.length < 2) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: 'Comparar',
      ctaSection: 'ofertas',
      ctaComponent: 'perfil-ofertas',
      ctaElementId: 'btn-ofertas-comparar',
      hu: '8209',
    });
    this.modal = 'comparar';
  }

  onComparar(oferta: OfertaPropuestaMock, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.idsSeleccionadas = toggleIdSeleccionado(
      this.idsSeleccionadas,
      oferta.idPropuesta,
      MAX_COMPARAR
    );
    this.idExpandida = null;
  }

  quitarComparar(idPropuesta: number): void {
    this.idsSeleccionadas = this.idsSeleccionadas.filter((id) => id !== idPropuesta);
  }

  onOrdenChange(orden: OrdenOfertas): void {
    this.orden = orden;
  }

  onOrden(orden: string): void {
    const label = this.ordenes.find((o) => o.id === orden)?.label ?? orden;
    this.analyticsTag.trackCtaClick({
      ctaLabel: label,
      ctaSection: 'ofertas',
      ctaComponent: 'perfil-ofertas',
      ctaElementId: 'btn-ofertas-ordenar',
      hu: '8209',
    });
    this.onOrdenChange(orden as OrdenOfertas);
  }

  onFiltroMonto(tick: number): void {
    this.onTickMonto(tick);
  }

  onFiltroTasa(tick: number): void {
    this.onTickTasa(tick);
  }

  onTickMonto(tick: number): void {
    if (!this.filtrosInteractivos) {
      return;
    }
    this.filtroMonto = resolverFiltroTick(tick, this.filtroMonto, TOPE_MONTO);
    this.sincronizarSeleccion();
  }

  onTickTasa(tick: number): void {
    if (!this.filtrosInteractivos) {
      return;
    }
    this.filtroTasa = resolverFiltroTick(tick, this.filtroTasa, TOPE_TASA);
    this.sincronizarSeleccion();
  }

  mostrarTodas(): void {
    this.filtroMonto = null;
    this.filtroTasa = null;
    this.sincronizarSeleccion();
  }

  onMostrarTodas(): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.copy.mostrarTodas,
      ctaSection: 'ofertas',
      ctaComponent: 'perfil-ofertas',
      ctaElementId: 'btn-ofertas-mostrar-todas',
      hu: '8209',
    });
    this.mostrarTodas();
  }

  toggleExpandida(id: number): void {
    this.idExpandida = this.idExpandida === id ? null : id;
  }

  toggleExpand(item: OfertaPropuestaMock): void {
    this.toggleExpandida(item.idPropuesta);
  }

  estaSeleccionada(item: OfertaPropuestaMock): boolean {
    return this.idsSeleccionadas.includes(item.idPropuesta);
  }

  iniciales(nombre: string): string {
    const parts = (nombre || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  totalGarantias(oferta: OfertaPropuestaMock): number {
    return (oferta.neoCargoComisiones ?? []).reduce((sum, c) => sum + (c.monto ?? 0), 0);
  }

  tieneGarantias(oferta: OfertaPropuestaMock | null | undefined): boolean {
    return (oferta?.neoCargoComisiones?.length ?? 0) > 0;
  }

  esTickMontoActivo(tick: number): boolean {
    return this.filtroMonto != null && tick <= this.filtroMonto;
  }

  esTickTasaActivo(tick: number): boolean {
    return this.filtroTasa != null && tick <= this.filtroTasa;
  }

  tickActivo(i: number, _total: number, tipo: 'monto' | 'tasa'): boolean {
    const ticks = tipo === 'monto' ? this.ticksMonto : this.ticksTasa;
    const tick = ticks[i];
    if (tick == null) {
      return false;
    }
    return tipo === 'monto' ? this.esTickMontoActivo(tick) : this.esTickTasaActivo(tick);
  }

  tickPct(i: number, total: number): number {
    if (total <= 1) {
      return 0;
    }
    return (i / (total - 1)) * 100;
  }

  fillWidth(fill: { start: number; end: number }): number {
    return Math.max(0, fill.end - fill.start);
  }

  dotEnRango(pct: number, fill: { start: number; end: number }): boolean {
    if (fill.end <= fill.start) {
      return false;
    }
    return pct >= fill.start - 0.01 && pct <= fill.end + 0.01;
  }

  esExpandida(item: OfertaPropuestaMock): boolean {
    return this.idExpandida === item.idPropuesta;
  }

  compararBloqueado(item: OfertaPropuestaMock): boolean {
    return !this.estaSeleccionada(item) && this.idsSeleccionadas.length >= MAX_COMPARAR;
  }

  labelTickMonto(tick: number): string {
    return esTickSinTecho(tick, TOPE_MONTO) ? `+${tick}M` : `${tick}M`;
  }

  labelTickTasa(tick: number): string {
    return esTickSinTecho(tick, TOPE_TASA) ? `+${tick}%` : `${tick}%`;
  }

  esExtremoRango(ticks: readonly number[], tick: number, filtro: number | null): boolean {
    if (filtro == null) {
      return false;
    }
    return tick === ticks[0] || tick === filtro;
  }

  pctTick(ticks: readonly number[], tick: number): number {
    const i = ticks.indexOf(tick);
    if (i < 0 || ticks.length <= 1) {
      return 0;
    }
    return (i / (ticks.length - 1)) * 100;
  }

  dinero(valor: number): string {
    return formatDineroCop(valor);
  }

  esBancoldex(oferta: OfertaPropuestaMock): boolean {
    return esOfertaBancoldex(oferta);
  }

  private sincronizarSeleccion(): void {
    this.idsSeleccionadas = idsPresentesEnLista(this.idsSeleccionadas, this.visibles);
    if (this.idExpandida != null && !this.visibles.some((o) => o.idPropuesta === this.idExpandida)) {
      this.idExpandida = null;
    }
  }

  private labelRango(
    ticks: readonly number[],
    filtro: number | null,
    label: (t: number) => string
  ): string {
    if (filtro == null) {
      return `${label(ticks[0])} - ${label(ticks[ticks.length - 1])}`;
    }
    return `${label(ticks[0])} - ${label(filtro)}`;
  }

  private pctEnRango(valor: number, min: number, max: number): number {
    if (max <= min) {
      return 0;
    }
    return Math.min(100, Math.max(0, ((valor - min) / (max - min)) * 100));
  }

  private cargar(): void {
    this.loading = true;
    this.error = false;
    this.microempresario.getPropuestas(this.idSolicitud).subscribe({
      next: (data: {
        propuestas?: OfertaPropuestaMock[];
        diasRestantes?: number;
        cantidadOfertas?: number;
      }) => {
        this.todas = Array.isArray(data?.propuestas) ? data.propuestas : [];
        const dias = data?.diasRestantes;
        this.diasRestantes = typeof dias === 'number' ? dias : null;
        const total = data?.cantidadOfertas;
        this.cantidadTotal = typeof total === 'number' ? total : this.todas.length;
        this.loading = false;
      },
      error: () => {
        this.todas = [];
        this.cantidadTotal = 0;
        this.loading = false;
        this.error = true;
      },
    });
  }

  private cargarParametrosModal(): void {
    this.cargarPar(PAR_078, (v) => (this.par078 = v || this.copy.par078Fallback));
    this.cargarPar(PAR_079, (v) => (this.par079 = v || this.copy.par079Fallback));
  }

  private cargarPar(idParametro: string, apply: (valor: string) => void): void {
    this.bancoldex
      .getFilterParametro({
        idParametro,
        nombre: null,
        isParFuncional: true,
        pagina: 1,
      })
      .subscribe({
        next: (response: { data?: { valorParametro?: string }[] }) => {
          const valor = response?.data?.[0]?.valorParametro;
          apply(typeof valor === 'string' ? valor : '');
        },
        error: () => apply(''),
      });
  }

  private cargarMotivos(): void {
    this.parametros.getTipoNegacionOfertas().subscribe({
      next: (response: any) => {
        const rows = Array.isArray(response) ? response : response?.data ?? [];
        this.motivos = rows.map((r: any) => ({
          id: Number(r.idTipoNegacionPropuestas),
          nombre: String(r.nombre || ''),
        }));
      },
      error: () => {
        this.motivos = [];
      },
    });
  }
}
